import React, { useState, useCallback, useEffect, type ReactNode } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { GrowthOpsContext } from './GrowthOpsContextObject';
import type {
  GrowthProvider,
  OptimizerAction,
  GrowthMetrics,
} from '../types';
import {
  fetchGrowthProviders,
  fetchOptimizerActions,
  fetchGrowthMetrics,
  forceToggleGrowthAds,
  forceTogglePause,
} from '../api';

export const GrowthOpsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { getToken } = useAuth();
  const { user } = useUser();
  const isPlatformAdmin = user?.publicMetadata?.is_platform_admin === true;
  const [providers, setProviders] = useState<GrowthProvider[]>([]);
  const [optimizerActions, setOptimizerActions] = useState<OptimizerAction[]>(
    []
  );
  const [metrics, setMetrics] = useState<GrowthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshProviders = useCallback(async () => {
    try {
      const data = await fetchGrowthProviders(isPlatformAdmin);
      setProviders(data);
    } catch (err) {
      console.error('Failed to fetch growth providers:', err);
      setError('Failed to load growth providers');
    }
  }, [isPlatformAdmin]);

  const refreshOptimizerActions = useCallback(
    async (businessId?: string) => {
      try {
        const data = await fetchOptimizerActions(100, businessId);
        setOptimizerActions(data);
      } catch (err) {
        console.error('Failed to fetch optimizer actions:', err);
        setError('Failed to load optimizer log');
      }
    },
    []
  );

  const refreshMetrics = useCallback(async () => {
    try {
      const data = await fetchGrowthMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('Failed to fetch growth metrics:', err);
      setError('Failed to load metrics');
    }
  }, []);

  const toggleGrowthAds = useCallback(
    async (businessId: string, enabled: boolean) => {
      try {
        // Enabling growth ads requires either a growth-plan org or a platform admin override
        if (enabled && !isPlatformAdmin) {
          const provider = providers.find((p) => p.id === businessId);
          const plan = provider?.subscriptionPlan?.toLowerCase();
          if (plan !== 'growth') {
            throw new Error(
              'Growth ads can only be enabled for orgs on the Growth plan. ' +
              'Platform admin override (is_platform_admin) is required for other plans.'
            );
          }
        }

        const authToken = await getToken({ template: 'supabase' });
        if (!authToken) {
          throw new Error('Unable to verify admin session');
        }

        await forceToggleGrowthAds(businessId, enabled, authToken);
        await refreshProviders();
      } catch (err) {
        console.error('Failed to toggle growth ads:', err);
        throw err;
      }
    },
    [getToken, isPlatformAdmin, providers, refreshProviders]
  );

  const togglePause = useCallback(
    async (businessId: string, paused: boolean) => {
      try {
        await forceTogglePause(businessId, paused);
        await refreshProviders();
      } catch (err) {
        console.error('Failed to toggle pause:', err);
        throw err;
      }
    },
    [refreshProviders]
  );

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([
        refreshProviders(),
        refreshMetrics(),
        refreshOptimizerActions(),
      ]);
      setLoading(false);
    };
    load();
  }, [refreshProviders, refreshMetrics, refreshOptimizerActions]);

  return (
    <GrowthOpsContext.Provider
      value={{
        providers,
        optimizerActions,
        metrics,
        loading,
        error,
        refreshProviders,
        refreshOptimizerActions,
        refreshMetrics,
        toggleGrowthAds,
        togglePause,
      }}
    >
      {children}
    </GrowthOpsContext.Provider>
  );
};
